import { handleEditor } from "@/webflow/detect-editor";

const BUTTON_SELECTOR = "[data-btn-hover], .button";
const CIRCLE_SELECTOR = ".btn-circle";
const MIN_CIRCLE_SCALE = 1.15;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getButtonFromTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const button = target.closest(BUTTON_SELECTOR);
  return button instanceof HTMLElement ? button : null;
}

function shouldHandlePointerTransition(button: HTMLElement, relatedTarget: EventTarget | null) {
  return !(relatedTarget instanceof Node && button.contains(relatedTarget));
}

function updateCirclePosition(button: HTMLElement, clientX: number, clientY: number) {
  const circle = button.querySelector<HTMLElement>(CIRCLE_SELECTOR);
  if (!circle) return;

  const buttonRect = button.getBoundingClientRect();
  const buttonWidth = buttonRect.width;
  const buttonHeight = buttonRect.height;
  if (!buttonWidth || !buttonHeight) return;

  // Keep origin on the nearest edge when pointer is outside.
  const localX = clamp(clientX - buttonRect.left, 0, buttonWidth);
  const localY = clamp(clientY - buttonRect.top, 0, buttonHeight);
  const offsetXFromLeft = (localX / buttonWidth) * 100;
  const offsetYFromTop = (localY / buttonHeight) * 100;

  // Size circle to always cover the furthest corner from the origin point.
  const distancesToCorners = [
    Math.hypot(localX, localY),
    Math.hypot(buttonWidth - localX, localY),
    Math.hypot(localX, buttonHeight - localY),
    Math.hypot(buttonWidth - localX, buttonHeight - localY),
  ];
  const maxDistance = Math.max(...distancesToCorners);
  const minDiameter = Math.max(buttonWidth, buttonHeight) * MIN_CIRCLE_SCALE;
  const diameter = Math.max(maxDistance * 2, minDiameter);

  circle.style.left = `${offsetXFromLeft.toFixed(1)}%`;
  circle.style.top = `${offsetYFromTop.toFixed(1)}%`;
  circle.style.width = `${diameter.toFixed(1)}px`;
}

export function initDirectionalButtonHover() {
  const onPointerOver = (event: PointerEvent) => {
    const button = getButtonFromTarget(event.target);
    if (!button) return;
    if (!shouldHandlePointerTransition(button, event.relatedTarget)) return;
    updateCirclePosition(button, event.clientX, event.clientY);
  };

  const onPointerOut = (event: PointerEvent) => {
    const button = getButtonFromTarget(event.target);
    if (!button) return;
    if (!shouldHandlePointerTransition(button, event.relatedTarget)) return;
    updateCirclePosition(button, event.clientX, event.clientY);
  };

  document.addEventListener("pointerover", onPointerOver);
  document.addEventListener("pointerout", onPointerOut);

  return () => {
    document.removeEventListener("pointerover", onPointerOver);
    document.removeEventListener("pointerout", onPointerOut);
  };
}

let destroyDirectionalButtonHover: (() => void) | null = null;

handleEditor((isEditor) => {
  if (isEditor) {
    destroyDirectionalButtonHover?.();
    destroyDirectionalButtonHover = null;
    return;
  }

  if (!destroyDirectionalButtonHover) {
    destroyDirectionalButtonHover = initDirectionalButtonHover();
  }
});
